import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

export interface ActionMenuItem {
  id: string;
  title: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  dividerAfter?: boolean;
}

interface ActionsMenuProps {
  actions: ActionMenuItem[];
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  menuProps?: {
    anchorOrigin?: {
      vertical: 'top' | 'center' | 'bottom';
      horizontal: 'left' | 'center' | 'right';
    };
    transformOrigin?: {
      vertical: 'top' | 'center' | 'bottom';
      horizontal: 'left' | 'center' | 'right';
    };
  };
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  actions,
  disabled = false,
  size = 'small',
  menuProps,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: ActionMenuItem) => {
    handleClose();
    if (!action.disabled) {
      action.onClick();
    }
  };

  return (
    <>
      <IconButton
        size={size}
        onClick={handleClick}
        disabled={disabled}
        sx={{
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <MoreVertIcon />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={menuProps?.anchorOrigin || {
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={menuProps?.transformOrigin || {
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 140,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
      >
        {actions.flatMap((action, index) => {
          const items = [
            <MenuItem
              key={action.id}
              onClick={() => handleMenuItemClick(action)}
              disabled={action.disabled}
              sx={{
                py: 1,
                px: 2,
                minHeight: 40,
                color: action.color && action.color !== 'inherit' 
                  ? `${action.color}.main` 
                  : 'text.primary',
                '&:hover': {
                  backgroundColor: action.color && action.color !== 'inherit' 
                    ? `${action.color}.50` 
                    : 'action.hover',
                },
                '& .MuiListItemIcon-root': {
                  color: action.color && action.color !== 'inherit' 
                    ? `${action.color}.main` 
                    : 'inherit',
                  minWidth: 36,
                },
              }}
            >
              {action.icon && (
                <ListItemIcon>
                  {action.icon}
                </ListItemIcon>
              )}
              <ListItemText 
                primary={action.title}
                slotProps={{
                  primary: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: action.color
                  }
                }}
                sx={{
                  ml: action.icon ? 0 : .5, // Adjust margin when no icon
                }}
              />
            </MenuItem>
          ];
          
          if (action.dividerAfter && index < actions.length - 1) {
            items.push(
              <Divider key={`${action.id}-divider`} sx={{ my: 0.5 }} />
            );
          }
          
          return items;
        })}
      </Menu>
    </>
  );
};